import React from 'react'
import Layout from '~layout'
// eslint-disable-next-line import/extensions
import * as StyledText from '~styles/Textual.styles'

const PrivacyPolicyPage: React.FC = () => (
  <Layout>
    <Layout.Content isCompact>
      <StyledText.RowContainer>
        <StyledText.Navigation>
          <StyledText.Link href="#coverage">What Does This Privacy Policy Cover?</StyledText.Link>
          <StyledText.Link href="#personalInfo">Personal Information You Provide to Us</StyledText.Link>
          <StyledText.Link href="#sharing">Will Unbounded Share Any of the Personal Information it Receives?</StyledText.Link>
          <StyledText.Link href="#login">Login Information</StyledText.Link>
          <StyledText.Link href="#anonymousData">Anonymous Data</StyledText.Link>
          <StyledText.Link href="#security">Security</StyledText.Link>
          <StyledText.Link href="#children">Children</StyledText.Link>
          <StyledText.Link href="#changes">Changes to this Privacy Policy</StyledText.Link>
          <StyledText.Link href="#tracking">Do Not Track</StyledText.Link>
          <StyledText.Link href="#emails">Email Communications & Opt-Out</StyledText.Link>
        </StyledText.Navigation>
        <StyledText.Text>
          <StyledText.Title>Website Privacy Policy</StyledText.Title>
          <StyledText.Date>Last updated: Feb 14, 2025</StyledText.Date>
          <StyledText.ParagraphStrong>
            Please read the following to learn more about our Privacy Policy. By visiting Unbounded’s web site, submitting information to Unbounded or
            using any of our products or services, you acknowledge that you accept the practices and policies outlined in this Privacy Policy.
          </StyledText.ParagraphStrong>

          <StyledText.Header id="coverage">1. What Does This Privacy Policy Cover?</StyledText.Header>
          <StyledText.Paragraph>
            This Privacy Policy covers Unbounded’s treatment of personally identifiable information (“Personal Information”) that you may provide to
            Unbounded when you use our website, products, or services.
          </StyledText.Paragraph>
          <StyledText.Paragraph>
            This policy does not apply to the practices of companies that Unbounded does not own or control, or to individuals that Unbounded does not
            employ or manage. If you provide any information to such third parties different rules regarding the collection and use of your personal
            information may apply. We strongly suggest you review such third party’s privacy policies before providing any data to them.
          </StyledText.Paragraph>

          <StyledText.Header id="personalInfo">2. Personal Information You Provide to Us</StyledText.Header>
          <StyledText.Paragraph>
            We may receive and store any information you enter on our web site or provide to us in any other way. The types of Personal Information
            collected include your full name, address, company, email address, telephone number, and other personally identifiable information you
            provided to use when using our products and services. The Personal Information you provide may be used for such purposes as responding to
            your requests for certain products and services, customizing the content you see, and communicating with you about our products and
            services.
          </StyledText.Paragraph>

          <StyledText.Header id="sharing">3. Will Unbounded Share Any of the Personal Information it Receives?</StyledText.Header>
          <StyledText.Paragraph>
            We neither rent nor sell your Personal Information to anyone. We share your Personal Information only with your consent or to business
            partners and affiliates as described below:
          </StyledText.Paragraph>
          <StyledText.Paragraph>
            <StyledText.Strong>Updates and Information:</StyledText.Strong>
            We may provide account holders with a newsletter, product updates and other information through the email address provided by the account
            holder.
          </StyledText.Paragraph>
          <StyledText.Paragraph>
            <StyledText.Strong>Support:</StyledText.Strong>
            We may make available capabilities that provide you the opportunity to submit your email address and/or phone number in order to receive
            support from us. We use that information solely to send you the information you requested via email. If you submit a support request, we
            may share information you provide in your support request, which could include your personal information, with our support providers, but
            only for the purpose of providing support to you.
          </StyledText.Paragraph>
          <StyledText.Paragraph>
            <StyledText.Strong>Account Registration and Use:</StyledText.Strong>
            If you utilize our products and services, you may be required to create an account. Through account registration we collect your name,
            your email address, address and password, which is used only to identify you with your account in order for you to use our products and
            services and to contact you with administrative message about our products and services.
          </StyledText.Paragraph>
          <StyledText.Paragraph>
            <StyledText.Strong>Agents:</StyledText.Strong>
            We may employ other companies and people to perform tasks on our behalf and need to share your information with them to provide products
            or services to you. Unbounded’s agents do not have any right to use Personal Information we share with them beyond what is necessary to
            assist us. You hereby consent to our sharing of Personal Information for the above purposes.
          </StyledText.Paragraph>
          <StyledText.Paragraph>
            <StyledText.Strong>Business Transfers:</StyledText.Strong>
            In some cases, we may choose to buy or sell business assets. In these types of transactions, customer information is typically one of the
            business assets that is transferred. Moreover, if Unbounded, or substantially all of its assets, were acquired, customer information would
            be one of the assets that is transferred or acquired by a third party. You acknowledge that such transfers may occur, and that any
            acquirer of Unbounded may continue to use your Personal Information as set forth in this policy.
          </StyledText.Paragraph>
          <StyledText.Paragraph>
            <StyledText.Strong>Protection of Unbounded and Others:</StyledText.Strong>
            We may release Personal Information when we believe in good faith that release is necessary to comply with any law; enforce or apply our
            terms of use and other agreements; or protect the rights, property, or safety of Unbounded, our employees, our customers, or others. This
            includes exchanging information with other companies and organizations for fraud protection and credit risk reduction.
          </StyledText.Paragraph>

          <StyledText.Header id="login">4. Login Information</StyledText.Header>
          <StyledText.Paragraph>
            We may collect third party service login information if you choose to provide it to us, such as Facebook login information (“Login
            Information”). Login Information is tokenized and is not stored on our servers. Tokenized Login Information enables association of your
            usage of our products and services with your third party service. Login Information is used only to enable your use of our products and
            services.
          </StyledText.Paragraph>

          <StyledText.Header id="anonymousData">5. Anonymous Data</StyledText.Header>
          <StyledText.Paragraph>
            We and our service providers collect anonymous data about use of our products, services and/or website. Anonymous data is data gathered in
            the background without direct input from the user, or is information gathered from user interactions, and which, in either case, does not
            identify someone individually (“Anonymous Data”). Anonymous Data does not include Personal Information. Anonymous Data may be gathered
            through our products and services or through cookies and similar technologies, such as tracking pixels on our websites, and analytics
            tools (collectively, “Tags”). You hereby expressly consent that we are entitled to gather such Anonymous Information and to place Tags on
            your computer or device and to use and disclose Anonymous Data, and to share it with third parties, as we deem appropriate.
          </StyledText.Paragraph>

          <StyledText.Header id="security">6. Security</StyledText.Header>
          <StyledText.Paragraph>
            Unbounded takes appropriate steps to ensure data privacy and security including through various hardware and software methodologies.
            However, due to the open communication nature of the Internet, we cannot guarantee that communications between you and the Unbounded
            website, and the Unbounded website and you, will be free from unauthorized access by third parties. Users of the Unbounded website do so
            at their own risk with respect to such communications.
          </StyledText.Paragraph>

          <StyledText.Header id="children">7. Children</StyledText.Header>
          <StyledText.Paragraph>
            Unbounded requests that children under the age of 18 not use the Unbounded website or products and services, nor submit any personal
            information to Unbounded. Unbounded does not knowingly collect personal information from children under the age of 13. Since information
            regarding children under the age of 13 is not collected, Unbounded does not knowingly use or distribute personal information regarding
            children under the age of 13. If Unbounded obtains actual knowledge that it has collected personal information about a child under the age
            of 13, that information will be immediately deleted from our database.
          </StyledText.Paragraph>

          <StyledText.Header id="changes">8. Changes to this Privacy Policy</StyledText.Header>
          <StyledText.Paragraph>
            Unbounded may amend this Privacy Policy from time to time. Use of information we collect now is subject to the Privacy Policy in effect at
            the time such information is collected. If we make changes in the way we use Personal Information, we will notify you by posting an
            amendment to the Privacy Policy on the website. Such modifications will become effective on the day they are posted. Unbounded encourages
            you to frequently review this Privacy Policy for any modifications.
          </StyledText.Paragraph>

          <StyledText.Header id="tracking">9. Do Not Track</StyledText.Header>
          <StyledText.Paragraph>
            Currently, we do not respond to “Do Not Track” browser features, but we may do so in the future as industry standards evolve.
          </StyledText.Paragraph>

          <StyledText.Header id="emails">10. Email Communications & Opt-Out</StyledText.Header>
          <StyledText.Paragraph>
            If you wish to: (i) remove your personally identifiable information from our system in order to stop receiving information from us, (ii)
            update your Personal Information, or (iii) have any questions about this privacy statement, the practices of this site, or your dealings
            with this website, please send us a detailed message (with sufficient information for us to identify your Personal Information) at
            unsubscribe at unbounded.com. We will make every effort to resolve your concerns. If you’ve registered with Unbounded online and request
            that all of your Personal Information be expunged, you acknowledge that Unbounded may terminate your account without any Unbounded service
            without liability to you.
          </StyledText.Paragraph>

          <StyledText.Address>
            © 2025{' '}
            <StyledText.Link href="https://unbounded.network" target="_blank" rel="noopener">
              https://unbounded.network
            </StyledText.Link>
          </StyledText.Address>
          <StyledText.Paragraph>All Rights Reserved.</StyledText.Paragraph>
        </StyledText.Text>
      </StyledText.RowContainer>
    </Layout.Content>
  </Layout>
)

export default PrivacyPolicyPage
